import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material';
import { Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  FormTemplateSchema,
  FormTemplateSchemaType
} from 'src/app/form-template/form-template-model';
import {
  ConsentClinicianPrefillDialogComponent,
  ConsentClinicianPrefillDialogResult
} from 'src/app/shared/consent-clinician-prefill-dialog/consent-clinician-prefill-dialog.component';
import {
  ConsentFormResponse,
  consentFormRequiresClinicianInput,
  consentFormResponseMatchesForm,
  getClinicianOnlyFields,
  getLatestConsentFormResponses
} from './consent-form.util';
import { QuestionnaireRequestService } from './questionnaire-request.service';

@Injectable({
  providedIn: 'root'
})
export class ConsentSendService {
  constructor(
    private matDialog: MatDialog,
    private questionnaireRequestService: QuestionnaireRequestService
  ) {}

  public getConsentFormKeyToSchemaMap(): { [key: string]: FormTemplateSchema } {
    return {
      ...this.questionnaireRequestService.defaultConsentFormKeyToSchemaMap,
      ...this.questionnaireRequestService.consentFormKeyToSchemaMap
    };
  }

  public getConsentFormKeyToFriendlyNameMap(): { [key: string]: string } {
    return {
      ...this.questionnaireRequestService.defaultConsentFormKeyToFriendlyNameMap,
      ...this.questionnaireRequestService.consentFormKeyToFriendlyNameMap
    };
  }

  public isConsentFormSchema(schema?: FormTemplateSchema | null): boolean {
    return !!(schema && schema.type === FormTemplateSchemaType.consent);
  }

  /**
   * Every consent form key that needs a clinician prefill before it can be sent. A batch sender
   * cannot answer a per-send prefill, so it blocks these keys outright.
   */
  public getConsentFormKeysRequiringClinicianInput(): string[] {
    const schemaMap = this.getConsentFormKeyToSchemaMap();
    return Object.keys(schemaMap).filter(schemaKey =>
      consentFormRequiresClinicianInput(schemaMap[schemaKey])
    );
  }

  /** True only when the patient's most recent completed response to this form was accepted. */
  public hasAcceptedConsentForSchema(schemaKey: string, patientQuestionnaires: any[]): boolean {
    const schema = this.getConsentFormKeyToSchemaMap()[schemaKey];
    if (!schema) {
      return false;
    }
    const schemaId = schema.id || schemaKey;
    return getLatestConsentFormResponses(patientQuestionnaires).some(
      response =>
        response.status === 'accepted' &&
        consentFormResponseMatchesForm(response, schemaId, schema.name)
    );
  }

  /** The send-dialog key for an answered consent form, or null if its template no longer exists. */
  public findConsentFormKey(response: ConsentFormResponse): string | null {
    const schemaMap = this.getConsentFormKeyToSchemaMap();
    const matchingKey = Object.keys(schemaMap).find(schemaKey => {
      const schema = schemaMap[schemaKey];
      return (
        !!schema && consentFormResponseMatchesForm(response, schema.id || schemaKey, schema.name)
      );
    });
    return matchingKey || null;
  }

  public openClinicianPrefillDialog(
    schema: FormTemplateSchema,
    defaultPractitionerId?: string,
    questionnaireDisplayName?: string
  ): Observable<ConsentClinicianPrefillDialogResult | null> {
    const clinicianOnlyFields = getClinicianOnlyFields(schema);
    if (!clinicianOnlyFields || clinicianOnlyFields.length === 0) {
      return of({ clinicianPrefill: null });
    }

    const resolvedDisplayName =
      (questionnaireDisplayName && questionnaireDisplayName.trim()) ||
      (schema && schema.name && String(schema.name).trim()) ||
      undefined;

    return ConsentClinicianPrefillDialogComponent.open(this.matDialog, {
      schema,
      defaultPractitionerId,
      questionnaireDisplayName: resolvedDisplayName
    })
      .afterClosed()
      .pipe(take(1));
  }
}
